import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heading, Subheading } from "../../../design/heading";
import { Button } from "../../../design/button";
import { Text } from "../../../design/text";
import { ErrorMessage, Field, FieldGroup } from "../../../design/fieldset";
import { Input } from "../../../design/input";
import { useBusiness } from "../../../contexts/BusinessContext";
import { useAccount } from "../../../contexts/AccountContext";
import { Card } from "../../../design/card";
import { MapPinIcon, MagnifyingGlassIcon, PencilSquareIcon } from "@heroicons/react/20/solid";
import PlaceAutocomplete from "../../../components/PlaceAutocomplete";

interface Address {
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Errors {
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  general?: string;
}

type InputMode = "google" | "manual";

// Helper function to parse address components from Google Places result
const parseAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[] | undefined
): Address => {
  if (!addressComponents) return {};

  const getComponent = (types: string[]): string => {
    const component = addressComponents.find((c) =>
      types.some((type) => c.types.includes(type))
    );
    return component?.long_name || "";
  };

  const getShortComponent = (types: string[]): string => {
    const component = addressComponents.find((c) =>
      types.some((type) => c.types.includes(type))
    );
    return component?.short_name || "";
  };

  const streetNumber = getComponent(["street_number"]);
  const route = getComponent(["route"]);
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ");

  return {
    streetAddress,
    streetAddress2: getComponent(["subpremise"]),
    city: getComponent(["locality", "sublocality", "administrative_area_level_3"]),
    state: getShortComponent(["administrative_area_level_1"]),
    zipCode: getComponent(["postal_code"]),
  };
};

const BusinessAddressStep: React.FC = () => {
  const navigate = useNavigate();
  const { account } = useAccount();
  const { updateBusiness } = useBusiness();
  const [data, setData] = useState<Address>({});
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>("google");
  const [selectedPlaceDescription, setSelectedPlaceDescription] = useState<string>("");

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!data.streetAddress) {
      newErrors.streetAddress = "Street Address is required";
    }
    if (!data.city) {
      newErrors.city = "City is required";
    }
    if (!data.state) {
      newErrors.state = "State is required";
    }
    if (!data.zipCode) {
      newErrors.zipCode = "Zip Code is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(data.zipCode)) {
      newErrors.zipCode = "Zip Code must be a valid format (e.g. 12345 or 12345-6789)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlaceSelect = async (place: google.maps.places.PlaceResult | null) => {
    if (!place) return;

    // Get address components from the place details
    const addressComponents = place.address_components;
    const parsedAddress = parseAddressComponents(addressComponents);

    setData(parsedAddress);
    setSelectedPlaceDescription(place.formatted_address || "");
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await updateBusiness(account.id, data);
      navigate("/onboarding/organization");
    } catch (e) {
      setErrors({ general: "Failed to save business address. Please try again." });
      console.error("Error saving business address:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/onboarding");
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-2 text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
            <MapPinIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
        <Heading>Business Address</Heading>
        <Text className="text-lg text-gray-600 dark:text-gray-400">
          Enter your business location
        </Text>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 text-xl">ℹ️</span>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <Text>
              This information will be used to populate the fields on your reports and invoices.
            </Text>
          </div>
        </div>
      </Card>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit}>
          {/* Input Mode Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setInputMode("google")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                inputMode === "google"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-400 dark:hover:bg-zinc-700"
              }`}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search
            </button>
            <button
              type="button"
              onClick={() => setInputMode("manual")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                inputMode === "manual"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-400 dark:hover:bg-zinc-700"
              }`}
            >
              <PencilSquareIcon className="h-4 w-4" />
              Manual Entry
            </button>
          </div>

          {errors.general && (
            <div className="mb-6 p-3 border border-red-200 rounded-lg bg-red-50 dark:border-red-800 dark:bg-red-900/20">
              <Text className="text-sm text-red-700 dark:text-red-300">
                {errors.general}
              </Text>
            </div>
          )}

          {/* Google Places Search */}
          {inputMode === "google" && (
            <div className="space-y-6">
              <Field>
                <Subheading>Search for your address</Subheading>
                <PlaceAutocomplete
                  onPlaceSelect={handlePlaceSelect}
                  placeholder="Start typing your business address..."
                />
              </Field>

              {/* Show selected address preview */}
              {selectedPlaceDescription && data.streetAddress && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <MapPinIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <Text className="font-medium text-green-800 dark:text-green-200">
                        Selected Address
                      </Text>
                      <Text className="text-sm text-green-700 dark:text-green-300 mt-1">
                        {selectedPlaceDescription}
                      </Text>
                    </div>
                  </div>
                </div>
              )}

              {/* Editable fields after selection */}
              {data.streetAddress && (
                <div className="pt-4 border-t border-gray-200 dark:border-zinc-700">
                  <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Review and edit if needed:
                  </Text>
                  <FieldGroup className="space-y-4">
                    <Field>
                      <Subheading>Street Address</Subheading>
                      <Input
                        value={data.streetAddress || ""}
                        onChange={(e) => setData({ ...data, streetAddress: e.target.value })}
                      />
                      {errors.streetAddress && (
                        <ErrorMessage>{errors.streetAddress}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Subheading>Street Address 2 (Optional)</Subheading>
                      <Input
                        placeholder="Suite, Unit, etc."
                        value={data.streetAddress2 || ""}
                        onChange={(e) => setData({ ...data, streetAddress2: e.target.value })}
                      />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field>
                        <Subheading>City</Subheading>
                        <Input
                          value={data.city || ""}
                          onChange={(e) => setData({ ...data, city: e.target.value })}
                        />
                        {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                      </Field>

                      <Field>
                        <Subheading>State</Subheading>
                        <Input
                          value={data.state || ""}
                          onChange={(e) => setData({ ...data, state: e.target.value })}
                        />
                        {errors.state && <ErrorMessage>{errors.state}</ErrorMessage>}
                      </Field>

                      <Field>
                        <Subheading>Zip Code</Subheading>
                        <Input
                          value={data.zipCode || ""}
                          onChange={(e) => setData({ ...data, zipCode: e.target.value })}
                        />
                        {errors.zipCode && <ErrorMessage>{errors.zipCode}</ErrorMessage>}
                      </Field>
                    </div>
                  </FieldGroup>
                </div>
              )}
            </div>
          )}

          {/* Manual Entry */}
          {inputMode === "manual" && (
            <FieldGroup className="space-y-6">
              <Field>
                <Subheading>Street Address</Subheading>
                <Input
                  id="businessStreet"
                  name="businessStreet"
                  placeholder="123 Main Street"
                  value={data.streetAddress || ""}
                  onChange={(e) => setData({ ...data, streetAddress: e.target.value })}
                />
                {errors.streetAddress && (
                  <ErrorMessage>{errors.streetAddress}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Subheading>Street Address 2 (Optional)</Subheading>
                <Input
                  id="businessStreet2"
                  name="businessStreet2"
                  placeholder="Suite, Unit, etc."
                  value={data.streetAddress2 || ""}
                  onChange={(e) => setData({ ...data, streetAddress2: e.target.value })}
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field>
                  <Subheading>City</Subheading>
                  <Input
                    id="businessCity"
                    name="businessCity"
                    placeholder="City"
                    value={data.city || ""}
                    onChange={(e) => setData({ ...data, city: e.target.value })}
                  />
                  {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                </Field>

                <Field>
                  <Subheading>State</Subheading>
                  <Input
                    id="businessState"
                    name="businessState"
                    placeholder="State"
                    value={data.state || ""}
                    onChange={(e) => setData({ ...data, state: e.target.value })}
                  />
                  {errors.state && <ErrorMessage>{errors.state}</ErrorMessage>}
                </Field>
              </div>

              <Field>
                <Subheading>Zip Code</Subheading>
                <Input
                  id="businessZip"
                  name="businessZip"
                  placeholder="12345"
                  value={data.zipCode || ""}
                  onChange={(e) => setData({ ...data, zipCode: e.target.value })}
                />
                {errors.zipCode && (
                  <ErrorMessage>{errors.zipCode}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          )}
        </form>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between gap-3">
        <Button
          outline
          className="flex-1 py-3 text-base"
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          color="blue"
          className="flex-1 py-3 text-base font-semibold"
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Continue"}
        </Button>
      </div>
    </div>
  );
};

export default BusinessAddressStep;
